<script setup lang="ts">
import { computed } from 'vue';
import { BENCHMARK_COLOUR_COMBOS } from './benchmark-colors';
import { DriverData } from '@Shared/types/portal/BenchmarkInterfaces';

interface Props {
  index: number;
  drivers: DriverData[];
  maxDriverCount: number;
}

const props = defineProps<Props>();
const getDriverBenchmarkScoreId = (driverIndex: number) =>
  `active-program-driver-benchmark-score-${props.index}-${driverIndex}`;
const getDriverTenantScoreId = (driverIndex: number) =>
  `active-program-driver-tenant-score-${props.index}-${driverIndex}`;
const getDriversTitleId = (driverIndex: number) => `active-program-gauge-title-${props.index}-${driverIndex}`;

const drivers = computed(() => props.drivers);
const getScoreBoxLeft = (score: number) => {
  // Clamp to 5% and 95% to ensure the box stays within the progress bar
  return Math.max(20, Math.min(80, score));
};
</script>

<template>
  <div class="benchmark-drivers-container bg-white rounded-xl p-6 shadow-sm">
    <!-- Header -->
    <div class="text-center mb-6">
      <h2 class="text-xl font-family[Poppins] font-bold text-#4F4F4F">CX Drivers</h2>
    </div>

    <!-- Driver Items -->
    <div class="space-y-10">
      <div v-for="(driver, driverIndex) in drivers" :key="driverIndex" class="driver-item">
        <!-- Title -->
        <div
          class="text-center font-bold text-lg font-semibold text-#4F4F4F mb-40px"
          :id="getDriversTitleId(driverIndex)"
        >
          {{ driver.friendlyName }}
        </div>

        <!-- Bar Container -->
        <div class="flex items-center gap-4">
          <!-- Bar Bar -->
          <div class="flex-1 relative">
            <!-- Background bar -->
            <div class="w-full h-3 bg-gray-200 rounded-full relative">
              <!-- Progress bar -->
              <div
                v-if="driver.benchmarkScore != null"
                class="h-full rounded-full relative"
                :style="{
                  width: `${driver.benchmarkScore}%`,
                  backgroundColor: BENCHMARK_COLOUR_COMBOS.ACTIVE_DRIVERS.progressBar,
                }"
              />

              <!-- Score position indicator (dot) -->
              <div
                v-if="driver.tenantScore != null"
                class="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20px h-20px rounded-full border-2 border-white shadow-sm"
                :style="{
                  left: `${driver.tenantScore}%`,
                  backgroundColor: BENCHMARK_COLOUR_COMBOS.ACTIVE_DRIVERS.dot,
                }"
              ></div>
              <!-- Your Score Box -->
              <div
                v-if="driver.tenantScore != null"
                class="absolute top-1/2 transform -translate-x-1/2 -translate-y-16 text-white px-3 py-1 border-2 border-white text-sm font-medium whitespace-nowrap shadow-sm rounded flex flex-col items-center justify-center"
                :style="{
                  left: `${getScoreBoxLeft(driver.tenantScore)}%`,
                  backgroundColor: BENCHMARK_COLOUR_COMBOS.ACTIVE_DRIVERS.scoreBox,
                }"
              >
                <div>YOUR SCORE:</div>
                <div :id="getDriverTenantScoreId(driverIndex)">{{ driver.tenantScore }}%</div>
              </div>
            </div>
          </div>

          <!-- Benchmark Score and Label -->
          <div class="text-center">
            <div v-if="driver.benchmarkScore != null" :id="getDriverBenchmarkScoreId(driverIndex)">
              <div class="text-4xl font-bold text-#4F4F4F">{{ driver.benchmarkScore }}%</div>
              <div class="text-12px uppercase tracking-wide">BENCHMARK</div>
            </div>
            <div v-else>
              <div class="text-4xl font-bold text-gray-400">-%</div>
              <div class="text-12px uppercase tracking-wide text-gray-400">BENCHMARK</div>
            </div>
          </div>
        </div>
      </div>
      <div v-for="n in maxDriverCount - drivers.length" :key="n" class="pt-32.5" />
    </div>
  </div>
</template>
